/**
 * Database Adapter Interface
 * Defines the contract for all database adapters
 */

/**
 * Database types supported by the system
 */
export const DATABASE_TYPES = {
  MONGODB: 'mongodb',
  HTTP: 'http',
  MOCK: 'mock'
}

/**
 * Query operators for filtering
 */
export const QUERY_OPERATORS = {
  EQUALS: '==',
  NOT_EQUALS: '!=',
  GREATER_THAN: '>',
  GREATER_THAN_OR_EQUAL: '>=',
  LESS_THAN: '<',
  LESS_THAN_OR_EQUAL: '<=',
  IN: 'in',
  NOT_IN: 'not_in',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  STARTS_WITH: 'starts_with',
  ENDS_WITH: 'ends_with',
  ARRAY_CONTAINS: 'array_contains',
  ARRAY_CONTAINS_ANY: 'array_contains_any'
}

/**
 * Sort directions
 */
export const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc'
}

/**
 * Database adapter interface
 * All database adapters must implement this interface
 */
export class IDatabaseAdapter {
  isConnected: boolean
  collectionName: string | null

  constructor() {
    this.isConnected = false
    this.collectionName = null
  }

  /**
   * Initialize the database connection
   * @param {Object} config - Database configuration
   * @returns {Promise<boolean>} - Connection success status
   */
  async initialize(config) {
    throw new Error('initialize() must be implemented by adapter')
  }

  /**
   * Get a collection reference
   * @param {string} collectionName - Name of the collection
   * @returns {Object} - Collection reference
   */
  collection(collectionName) {
    throw new Error('collection() must be implemented by adapter')
  }

  /**
   * Get a single document by ID
   * @param {string} collectionName - Name of the collection
   * @param {string} id - Document ID
   * @returns {Promise<Object|null>} - Document or null if not found
   */
  async get(collectionName, id) {
    throw new Error('get() must be implemented by adapter')
  }

  /**
   * List documents with optional filtering and pagination
   * @param {string} collectionName - Name of the collection
   * @param {Object} options - Query options
   * @param {Array} options.filters - Array of filter conditions
   * @param {Array} options.orderBy - Array of sort conditions
   * @param {number} options.limit - Maximum number of documents
   * @param {number} options.offset - Number of documents to skip
   * @returns {Promise<Array>} - Array of documents
   */
  async list(collectionName: string, options: any = {}): Promise<any[]> {
    throw new Error('list() must be implemented by adapter')
  }

  /**
   * Query documents with complex conditions
   * @param {string} collectionName - Name of the collection
   * @param {Array} conditions - Array of query conditions
   * @param {Object} options - Query options
   * @returns {Promise<Array>} - Array of documents
   */
  async query(collectionName: string, conditions: any[], options: any = {}): Promise<any[]> {
    throw new Error('query() must be implemented by adapter')
  }

  /**
   * Create a new document
   * @param {string} collectionName - Name of the collection
   * @param {Object} data - Document data
   * @param {string} id - Optional custom ID
   * @returns {Promise<Object>} - Created document with ID
   */
  async create(collectionName, data, id = null) {
    throw new Error('create() must be implemented by adapter')
  }

  /**
   * Update an existing document
   * @param {string} collectionName - Name of the collection
   * @param {string} id - Document ID
   * @param {Object} data - Update data
   * @param {boolean} merge - Whether to merge with existing data
   * @returns {Promise<Object>} - Updated document
   */
  async update(collectionName, id, data, merge = true) {
    throw new Error('update() must be implemented by adapter')
  }

  /**
   * Create or update a document
   * @param {string} collectionName - Name of the collection
   * @param {string} id - Document ID
   * @param {Object} data - Document data
   * @returns {Promise<Object>} - Upserted document
   */
  async upsert(collectionName, id, data) {
    throw new Error('upsert() must be implemented by adapter')
  }

  /**
   * Delete a document
   * @param {string} collectionName - Name of the collection
   * @param {string} id - Document ID
   * @returns {Promise<boolean>} - Deletion success status
   */
  async delete(collectionName: string, id: string): Promise<boolean> {
    throw new Error('delete() must be implemented by adapter')
  }

  /**
   * Execute batch operations
   * @param {Array} operations - Array of batch operations
   * @returns {Promise<Array>} - Results of batch operations
   */
  async batch(operations: any[]): Promise<any[]> {
    throw new Error('batch() must be implemented by adapter')
  }

  /**
   * Listen to real-time changes (no-op for non-realtime adapters)
   * @param {string} collectionName - Name of the collection
   * @param {Object} options - Listen options
   * @param {Function} callback - Change callback
   * @returns {Function} - Unsubscribe function
   */
  onSnapshot(collectionName, options, callback) {
    // Default no-op implementation
    return () => {}
  }

  /**
   * Search documents with text search
   * @param {string} collectionName - Name of the collection
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} - Array of matching documents
   */
  async searchDocuments(collectionName: string, searchTerm: string, options: any = {}): Promise<any[]> {
    throw new Error('searchDocuments() must be implemented by adapter')
  }

  /**
   * Get connection status
   * @returns {boolean} - Connection status
   */
  getConnectionStatus() {
    return this.isConnected
  }

  /**
   * Close the database connection
   * @returns {Promise<void>}
   */
  async close() {
    throw new Error('close() must be implemented by adapter')
  }
}

/**
 * Query condition helper
 * @param {string} field - Field name
 * @param {string} operator - Query operator
 * @param {*} value - Value to compare
 * @returns {Object} - Query condition
 */
export const createCondition = (field, operator, value) => ({
  field,
  operator,
  value
})

/**
 * Sort condition helper
 * @param {string} field - Field name
 * @param {string} direction - Sort direction
 * @returns {Object} - Sort condition
 */
export const createSort = (field, direction = SORT_DIRECTION.ASC) => ({
  field,
  direction
})

/**
 * Pagination helper
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Items per page
 * @returns {Object} - Pagination options
 */
export const createPagination = (page = 1, limit = 20) => ({
  offset: (page - 1) * limit,
  limit
})